import { useEffect, useRef } from 'react';
import { useNotifications } from '@/shared/contexts/NotificationContext.new';
import { apiClient } from '@/shared/services/api';
import { API_ROUTES } from '@/shared/services/apiRoutes';
import { getAccessToken } from '@/shared/utils/supabaseStorage';

interface CallNotification {
  callId: string;
  notificationId5: string;
  notificationId2: string;
  notificationId1: string;
  callTime: Date;
  customerName: string;
  has5MinNotification: boolean;
  has2MinNotification: boolean;
  has1MinNotification: boolean;
}

export function useCallNotifications() {
  const { addNotification, removeNotification, notifications } = useNotifications();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const activeCallNotifications = useRef<Map<string, CallNotification>>(new Map());

  useEffect(() => {
    const checkCallNotifications = async () => {
      // Skip if user is not authenticated
      const accessToken = getAccessToken();
      if (!accessToken) {
        return;
      }

      try {
        const now = new Date();
        const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000);

        // SECURITY: Use backend API to fetch upcoming calls
        const response = await apiClient.get<{
          ok: boolean;
          events: any[];
        }>(`${API_ROUTES.CALENDAR_EVENTS}?event_type=scheduled-call&start_time_gte=${now.toISOString()}&start_time_lte=${thirtyMinutesFromNow.toISOString()}`);

        if (!response.ok) {
          console.error('[useCallNotifications] Failed to fetch upcoming calls');
          return;
        }

        const upcomingCalls = response.events || [];

        // Process each upcoming call
        upcomingCalls.forEach(call => {
          const callTime = new Date(call.start_time);
          const minutesUntilCall = Math.round((callTime.getTime() - now.getTime()) / (1000 * 60));
          const callId = call.id;
          
          // Get or create tracking for this call
          let callNotification = activeCallNotifications.current.get(callId);
          if (!callNotification) {
            callNotification = {
              callId,
              notificationId5: `call-reminder-5-${callId}`,
              notificationId2: `call-reminder-2-${callId}`,
              notificationId1: `call-reminder-1-${callId}`,
              callTime,
              customerName: call.customer_name || 'customer',
              has5MinNotification: false,
              has2MinNotification: false,
              has1MinNotification: false
            };
            activeCallNotifications.current.set(callId, callNotification);
          }

          // Check if we should show 5-minute notification
          if (minutesUntilCall <= 5 && minutesUntilCall > 2 && !callNotification.has5MinNotification) {
            console.log('[useCallNotifications] Creating 5-min notification for call:', call.id);
            addNotification({
              type: "call-reminder",
              title: "Call Starting Soon",
              message: `Your call with ${call.customer_name || 'customer'} starts in ${minutesUntilCall} minute${minutesUntilCall !== 1 ? 's' : ''}`,
              action_url: `/calendar?event=${call.id}` // Opens calendar modal which has prejoin link
            });
            callNotification.has5MinNotification = true;
          }

          // Check if we should show 2-minute notification
          if (minutesUntilCall <= 2 && minutesUntilCall > 1 && !callNotification.has2MinNotification) {
            console.log('[useCallNotifications] Creating 2-min notification for call:', call.id);
            addNotification({
              type: "call-reminder",
              title: "Join Your Call Now",
              message: `Your call with ${call.customer_name || 'customer'} starts in ${minutesUntilCall} minute${minutesUntilCall !== 1 ? 's' : ''}`,
              action_url: `/calendar?event=${call.id}` // Opens calendar modal which has prejoin link
            });
            callNotification.has2MinNotification = true;
          }

          // Check if we should show 1-minute notification
          if (minutesUntilCall <= 1 && minutesUntilCall >= 0 && !callNotification.has1MinNotification) {
            console.log('[useCallNotifications] Creating 1-min notification for call:', call.id);
            addNotification({
              type: "call-reminder",
              title: "Join Your Call Now",
              message: `Your call with ${call.customer_name || 'customer'} starts in ${minutesUntilCall} minute${minutesUntilCall !== 1 ? 's' : ''}`,
              action_url: `/calendar?event=${call.id}` // Opens calendar modal which has prejoin link
            });
            callNotification.has1MinNotification = true;
          }
        });

        // Check for notifications that should be removed (15 minutes after call time)
        const notificationsToRemove: string[] = [];
        
        activeCallNotifications.current.forEach((callNotif, callId) => {
          const minutesSinceCall = Math.round((now.getTime() - callNotif.callTime.getTime()) / (1000 * 60));
          
          // Remove notification if call was more than 15 minutes ago
          if (minutesSinceCall > 15) {
            // Find the notification in the current notifications and remove it
            const notification = notifications.find(n => 
              n.type === 'call-reminder' && 
              n.message.includes(callNotif.customerName)
            );
            
            if (notification) {
              removeNotification(notification.id);
            }
            
            notificationsToRemove.push(callId);
          }
        });

        // Clean up tracking for removed notifications
        notificationsToRemove.forEach(callId => {
          activeCallNotifications.current.delete(callId);
        });

      } catch (error) {
        console.error('Error checking call notifications:', error);
      }
    };

    // Initial check
    checkCallNotifications();

    // Set up interval to check every minute
    intervalRef.current = setInterval(checkCallNotifications, 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [addNotification, removeNotification, notifications]);

  // Clean up tracking when component unmounts
  useEffect(() => {
    return () => {
      activeCallNotifications.current.clear();
    };
  }, []);
}